/**
 * The header's ▶ Executar: the run it started in the active repository, followed until it ends.
 *
 * The order's id is kept for the tab (sessionStorage), so a reload keeps following it. While the
 * order runs, its current step says where it is: "waiting" for an agent to take it (a clock), or
 * "working" (■ stop). When it ends, the address the agent reported (`DEVPULSE-OPEN: <url>`) opens:
 * on the desktop the app's runner opens it as the agent finishes, so here only in a browser, and
 * only when the desktop app is not running there too — never two tabs.
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { localAddressToOpen } from './localRunner'
import type { WorkOrder } from './types'

export type RunPhase = 'idle' | 'waiting' | 'working'

const KEY = 'devpulse.run.order.'

function storedOrder(repositoryId: string | null, kind: string): string | null {
  if (!repositoryId) return null
  try {
    return sessionStorage.getItem(`${KEY}${kind === 'run' ? '' : `${kind}.`}${repositoryId}`)
  } catch {
    return null
  }
}

function storeOrder(repositoryId: string, kind: string, orderId: string | null) {
  const key = `${KEY}${kind === 'run' ? '' : `${kind}.`}${repositoryId}`
  try {
    if (orderId) sessionStorage.setItem(key, orderId)
    else sessionStorage.removeItem(key)
  } catch {
    // The run is followed until the page closes, then.
  }
}

/** Where a run is, from its order. */
export function phaseOf(order: WorkOrder | null | undefined): RunPhase {
  if (!order || order.status !== 'Running') return 'idle'
  return order.step === 'working' ? 'working' : 'waiting'
}

/**
 * Why a finished run did not run anything, when the agent said so: the text after its
 * `DEVPULSE-STATUS: PENDING —` line (a "Once" order ends Done even then). Null when it ran.
 */
export function notRunReason(order: WorkOrder | null | undefined): string | null {
  const text = order?.lastReply ?? order?.lastReport ?? ''
  const match = /DEVPULSE-STATUS:\s*PENDING\s*[—-]?\s*(.*)/i.exec(text)
  if (!match) return null
  const reason = match[1]!.trim()
  return reason.length > 140 ? `${reason.slice(0, 139)}…` : reason
}

/** The local address a finished run reported, if any. */
export function addressOf(order: WorkOrder | null | undefined): string | null {
  if (!order || order.status !== 'Done') return null
  return localAddressToOpen(order.lastReply ?? order.lastReport ?? '')
}

/**
 * `kind` keeps each header button's run apart: ▶ Executar ("run"), Analisar ("analyze"), O que falta
 * ("launch") can each be under way in the same repository.
 */
export function useRepositoryRun(repositoryId: string | null, onFinished: (order: WorkOrder) => void, kind = 'run') {
  const [orderId, setOrderId] = useState<string | null>(() => storedOrder(repositoryId, kind))
  useEffect(() => setOrderId(storedOrder(repositoryId, kind)), [repositoryId, kind])

  const { data: order } = useQuery({
    queryKey: ['run-order', kind, orderId],
    queryFn: async () => (await api.workOrders.list()).find((o) => o.id === orderId) ?? null,
    enabled: Boolean(orderId),
    refetchInterval: (query) => ((query.state.data as WorkOrder | null | undefined)?.status === 'Running' || query.state.data === undefined ? 3_000 : false),
  })

  // A run that ended is reported once, then forgotten: the button is ▶ again.
  const reported = useRef<string | null>(null)
  useEffect(() => {
    if (!order || order.status === 'Running' || reported.current === order.id) return
    reported.current = order.id
    if (repositoryId) storeOrder(repositoryId, kind, null)
    setOrderId(null)
    onFinished(order)
  }, [order, repositoryId, onFinished])
  useEffect(() => {
    // An order that no longer exists (deleted, another account) stops being followed.
    if (orderId && order === null && repositoryId) {
      storeOrder(repositoryId, kind, null)
      setOrderId(null)
    }
  }, [order, orderId, repositoryId])

  return {
    order: order ?? null,
    phase: orderId && order === undefined ? ('waiting' as RunPhase) : phaseOf(order),
    follow: (id: string) => {
      if (repositoryId) storeOrder(repositoryId, kind, id)
      reported.current = null
      setOrderId(id)
    },
  }
}

/** What an analysis returned: the report, and the fix prompt it wrote between <vephus-fix-prompt> tags. */
export function analysisOf(text: string): { report: string; fixPrompt: string | null } {
  const match = /<vephus-fix-prompt>\s*([\s\S]*?)\s*<\/vephus-fix-prompt>/i.exec(text)
  const fixPrompt = match?.[1]?.trim() || null
  const report = (match ? text.replace(match[0], '') : text)
    .replace(/DEVPULSE-STATUS:.*$/gim, '')
    .replace(/^#+\s*Prompt de corre[cç][aã]o\s*$/gim, '')
    .trim()
  return { report, fixPrompt }
}

export type LaunchStage = 'mvp' | 'deploy' | 'lojas' | 'dominio' | 'producao'

/** What "O que falta" returned: the overall readiness, whether there is an MVP, the next stage, and the rest. */
export function readinessOf(text: string): { ready: number | null; mvp: boolean | null; stage: LaunchStage | null; body: string } {
  const ready = /VEPHUS-READY:\s*(\d{1,3})/i.exec(text)
  const mvp = /VEPHUS-MVP:\s*(sim|n[aã]o|yes|no)/i.exec(text)
  const stage = /VEPHUS-STAGE:\s*(mvp|deploy|lojas|dominio|domínio|producao|produção)/i.exec(text)
  const value = ready ? Math.max(0, Math.min(100, Number(ready[1]))) : null
  const stageValue = stage ? (stage[1]!.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') as LaunchStage) : null
  const body = text
    .replace(/^VEPHUS-(READY|MVP|STAGE):.*$/gim, '')
    .replace(/DEVPULSE-STATUS:.*$/gim, '')
    .trim()
  return { ready: value, mvp: mvp ? /^(sim|yes)$/i.test(mvp[1]!) : null, stage: stageValue, body }
}
